import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import config, { isDefaultCluster } from '../Config';
import { authFetch } from '../functions/authFetch';
import { HandleResponse } from '../functions/HandleResponse';
import { downloadJobLog, formatLogSize } from '../functions/jobLog';
import './pages.css';

const TERMINATED = ['Completed', 'Failed', 'OOMKilled', 'Error', 'ContainerCannotRun', 'DeadlineExceeded', 'Evicted', 'Killed', 'CrashLoopBackOff'];

const FREQS = [
  { id: 'minutes', label: 'Minutes' },
  { id: 'hourly', label: 'Hourly' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
];

const WEEKDAYS = [
  { value: 0, short: 'Sun', label: 'Sunday' },
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
];

// IANA timezone list (browser-provided). Fall back to a curated subset on older browsers.
const TIMEZONES = (() => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      const list = Intl.supportedValuesOf('timeZone');
      return ['UTC', ...list.filter((z) => z !== 'UTC')];
    }
  } catch { /* ignore */ }
  return ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Shanghai',
    'Asia/Tokyo', 'Australia/Sydney'];
})();

const isValidTimezone = (tz) => {
  if (!tz) return false;
  if (TIMEZONES.includes(tz)) return true;
  try { Intl.DateTimeFormat(undefined, { timeZone: tz }); return true; } catch { return false; }
};

const clampInt = (v, min, max) => {
  const n = parseInt(v, 10);
  if (isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
};
const pad2 = (n) => String(n).padStart(2, '0');

// ── cron expression validation (standard 5-field) ──
const FIELD_RANGES = [[0, 59], [0, 23], [1, 31], [1, 12], [0, 7]];
const validCronField = (field, [min, max]) => {
  if (field === '*') return true;
  return field.split(',').every((part) => {
    if (!part) return false;
    let range = part;
    if (part.includes('/')) {
      const [r, step] = part.split('/');
      if (!/^\d+$/.test(step) || +step < 1) return false;
      range = r;
    }
    if (range === '*') return true;
    if (range.includes('-')) {
      const [a, b] = range.split('-');
      if (!/^\d+$/.test(a) || !/^\d+$/.test(b)) return false;
      return +a >= min && +b <= max && +a <= +b;
    }
    if (!/^\d+$/.test(range)) return false;
    return +range >= min && +range <= max;
  });
};
const validateCron = (expr) => {
  const f = (expr || '').trim().split(/\s+/);
  if (f.length !== 5) return false;
  return f.every((field, i) => validCronField(field, FIELD_RANGES[i]));
};

// ── build a cron string from the builder state ──
const buildCron = (f) => {
  const [h, m] = (f.timeOfDay || '00:00').split(':').map((n) => clampInt(n, 0, 59));
  const hour = clampInt(h, 0, 23);
  switch (f.freq) {
    case 'minutes': return `*/${clampInt(f.everyMinutes, 1, 59)} * * * *`;
    case 'hourly':  return `${clampInt(f.hourlyMinute, 0, 59)} */${clampInt(f.everyHours, 1, 23)} * * *`;
    case 'daily':   return `${m} ${hour} * * *`;
    case 'weekly': {
      const days = [...(f.weekdays || [])].sort((a, b) => a - b);
      return `${m} ${hour} * * ${days.length ? days.join(',') : '*'}`;
    }
    case 'monthly': return f.monthDay === 'last' ? `${m} ${hour} 28-31 * *` : `${m} ${hour} 1 * *`;
    default: return '';
  }
};

// ── best-effort parse of a cron string back into builder state ──
const parseCron = (expr) => {
  const f = (expr || '').trim().split(/\s+/);
  if (f.length !== 5) return null;
  const [mi, ho, dom, mon, dow] = f;
  if (mon !== '*') return null;
  const isNum = (s) => /^\d+$/.test(s);
  if (/^\*\/\d+$/.test(mi) && ho === '*' && dom === '*' && dow === '*') {
    return { freq: 'minutes', everyMinutes: +mi.split('/')[1] };
  }
  if (isNum(mi) && /^\*\/\d+$/.test(ho) && dom === '*' && dow === '*') {
    return { freq: 'hourly', hourlyMinute: +mi, everyHours: +ho.split('/')[1] };
  }
  if (isNum(mi) && isNum(ho) && +mi <= 59 && +ho <= 23) {
    const time = `${pad2(+ho)}:${pad2(+mi)}`;
    if (dom === '*' && dow === '*') return { freq: 'daily', timeOfDay: time };
    if (dom === '*' && /^[0-7](,[0-7])*$/.test(dow)) {
      const days = [...new Set(dow.split(',').map((d) => (+d === 7 ? 0 : +d)))];
      return { freq: 'weekly', weekdays: days, timeOfDay: time };
    }
    if (dom === '1' && dow === '*') return { freq: 'monthly', monthDay: 'first', timeOfDay: time };
    if (dom === '28-31' && dow === '*') return { freq: 'monthly', monthDay: 'last', timeOfDay: time };
  }
  return null;
};

// ── human-readable summary of a cron string ──
const humanizeSchedule = (expr) => {
  const p = parseCron(expr);
  if (!p) return validateCron(expr) ? 'Custom schedule' : 'Invalid schedule';
  const t = p.timeOfDay;
  switch (p.freq) {
    case 'minutes': return `Every ${p.everyMinutes} minute${p.everyMinutes > 1 ? 's' : ''}`;
    case 'hourly':  return `Every ${p.everyHours} hour${p.everyHours > 1 ? 's' : ''} at :${pad2(p.hourlyMinute)}`;
    case 'daily':   return `Every day at ${t}`;
    case 'weekly': {
      const names = p.weekdays.sort((a, b) => a - b).map((d) => WEEKDAYS.find((w) => w.value === d)?.short).join(', ');
      return `Every ${names || '—'} at ${t}`;
    }
    case 'monthly': return `${p.monthDay === 'last' ? 'Last day' : '1st'} of every month at ${t}`;
    default: return 'Custom schedule';
  }
};

const fmtTime = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch { return String(v); }
};

const relTime = (v) => {
  if (!v) return '';
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    let sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) sec = 0;
    const units = [['y', 31536000], ['mo', 2592000], ['d', 86400], ['h', 3600], ['m', 60]];
    for (const [label, s] of units) {
      const val = Math.floor(sec / s);
      if (val >= 1) return `${val}${label} ago`;
    }
    return `${sec}s ago`;
  } catch { return ''; }
};

const statusClass = (status) => {
  if (status === 'Completed') return 'ok';
  if (TERMINATED.includes(status)) return 'fail';
  return 'pending';
};

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad2(i));

// Flat commandArgs (e.g. ["--email", "a@b.com"]) → flag-name/value pairs for the UI.
const parseArgs = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return [{ name: '', value: '' }];
  const rows = [];
  for (let i = 0; i < arr.length; i += 1) {
    const tok = String(arr[i]);
    if (tok.startsWith('-')) {
      const name = tok.replace(/^-+/, '');
      let value = '';
      if (i + 1 < arr.length && !String(arr[i + 1]).startsWith('-')) {
        value = String(arr[i + 1]);
        i += 1;
      }
      rows.push({ name, value });
    } else {
      // Legacy positional token with no flag; surface it in the value column.
      rows.push({ name: '', value: tok });
    }
  }
  return rows.length ? rows : [{ name: '', value: '' }];
};

// Fully custom HH:MM picker. Native <select>/<input type=time> popups can't be
// styled, so we render our own dropdown with scrollable hour/minute columns.
function TimeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const hourRef = useRef(null);
  const minRef = useRef(null);
  const [h, m] = (value || '00:00').split(':');
  const hh = pad2(parseInt(h, 10) || 0);
  const mm = pad2(parseInt(m, 10) || 0);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    const id = requestAnimationFrame(() => {
      hourRef.current?.querySelector('.cron-tp-opt.sel')?.scrollIntoView({ block: 'center' });
      minRef.current?.querySelector('.cron-tp-opt.sel')?.scrollIntoView({ block: 'center' });
    });
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(id);
    };
  }, [open]);

  return (
    <span className="cron-tp" ref={ref}>
      <button type="button" className={`cron-tp-trigger ${open ? 'open' : ''}`} onClick={() => setOpen((o) => !o)}>
        <span className="cron-tp-val">{hh}:{mm}</span>
        <svg className="cron-tp-chev" width="11" height="11" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="cron-tp-pop">
          <div className="cron-tp-group">
            <div className="cron-tp-head">Hr</div>
            <div className="cron-tp-col" ref={hourRef}>
              {HOURS.map((x) => (
                <button type="button" key={x} className={`cron-tp-opt ${x === hh ? 'sel' : ''}`}
                  onClick={() => onChange(`${x}:${mm}`)}>{x}</button>
              ))}
            </div>
          </div>
          <div className="cron-tp-group">
            <div className="cron-tp-head">Min</div>
            <div className="cron-tp-col" ref={minRef}>
              {MINUTES.map((x) => (
                <button type="button" key={x} className={`cron-tp-opt ${x === mm ? 'sel' : ''}`}
                  onClick={() => onChange(`${hh}:${x}`)}>{x}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

const emptyForm = () => ({
  name: '',
  // schedule builder state
  scheduleMode: 'ui',      // 'ui' | 'cron'
  schedule: '*/5 * * * *', // raw cron string (used when scheduleMode === 'cron')
  freq: 'daily',
  everyMinutes: 5,
  everyHours: 1,
  hourlyMinute: 0,
  timeOfDay: '09:00',
  weekdays: [1],
  monthDay: 'first',     // 'first' (1st) | 'last' (last day of month)
  image: '',
  timezone: 'UTC',
  args: [{ name: '', value: '' }],
  env: [{ name: '', value: '' }],
  useConfigMap: false,
  configMapName: '',
  useSecret: false,
  secretName: '',
  useResources: false,
  cpu: '0.5',
  memory: '0.25',
  useGpu: false,
  enabled: true,
});

export default function CronManagement() {
  const navigate = useNavigate();
  const base = config.RUNNER_API;
  const isDedicated = !isDefaultCluster();

  const [crons, setCrons] = useState([]);
  const [cronsLoading, setCronsLoading] = useState(false);
  const [selectedCron, setSelectedCron] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunStatus, setSelectedRunStatus] = useState(null);
  const [logContent, setLogContent] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  // Set when only the tail of an oversized log is on screen: { totalBytes }
  const [logTruncated, setLogTruncated] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // form / modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = create
  const [form, setForm] = useState(emptyForm());
  const [mcrImages, setMcrImages] = useState([]);
  const [mcrLoading, setMcrLoading] = useState(false);
  const [configMaps, setConfigMaps] = useState([]);
  const [configMapsLoading, setConfigMapsLoading] = useState(false);
  const [secrets, setSecrets] = useState([]);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // GPU cron jobs are Premium only; the toggle stays hidden and unsent elsewhere.
  const [clusterTier, setClusterTier] = useState(null);
  const gpuAvailable = clusterTier === 'prod';

  const [banner, setBanner] = useState(null); // { type, text }

  // delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(null); // cron pending deletion
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // ── data loading ──────────────────────────────────────────────────────────
  const fetchCrons = useCallback(async () => {
    setCronsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/cron/list`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        setCrons(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setCronsLoading(false); }
  }, [base, navigate]);

  const fetchHistory = useCallback(async (cronId) => {
    setHistoryLoading(true);
    setHistory([]);
    try {
      const res = await authFetch(`${base}/v1/cron/history/${encodeURIComponent(cronId)}?limit=10`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        setHistory(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  }, [base, navigate]);

  const fetchMcrImages = useCallback(async () => {
    setMcrLoading(true);
    try {
      const res = await authFetch(`${base}/v1/cdci/mcr/images?image_type=short`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setMcrImages(Array.isArray(data) ? data.map(i => i.image_name || i) : []);
      }
    }     catch { /* ignore */ }
    finally { setMcrLoading(false); }
  }, [base]);

  const fetchConfigMaps = useCallback(async () => {
    setConfigMapsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/deployment/list-configmap`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setConfigMaps(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setConfigMapsLoading(false); }
  }, [base]);

  const fetchSecrets = useCallback(async () => {
    setSecretsLoading(true);
    try {
      const res = await authFetch(`${base}/v1/secret/list`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setSecrets(Array.isArray(data) ? data : []);
      }
    } catch { /* ignore */ }
    finally { setSecretsLoading(false); }
  }, [base]);

  useEffect(() => { fetchCrons(); }, [fetchCrons]);

  useEffect(() => {
    authFetch(`${base}/v1/deployment/get-cluster-tier`, { method: 'GET' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.tier) setClusterTier(d.tier); })
      .catch(() => {});
  }, [base]);

  useEffect(() => {
    if (modalOpen && form.useConfigMap && configMaps.length === 0) fetchConfigMaps();
  }, [modalOpen, form.useConfigMap, fetchConfigMaps, configMaps.length]);

  useEffect(() => {
    if (modalOpen && form.useSecret && secrets.length === 0) fetchSecrets();
  }, [modalOpen, form.useSecret, fetchSecrets, secrets.length]);

  const selectCron = (cron) => {
    setSelectedCron(cron);
    setSelectedRun(null);
    setSelectedRunStatus(null);
    setLogContent(null);
    setLogTruncated(null);
    fetchHistory(cron.cron_id);
  };

  const selectRun = async (run) => {
    setSelectedRun(run.JobID);
    setSelectedRunStatus(run.Status || 'pending');
    setLogLoading(true);
    setLogContent(['Loading…']);
    setLogTruncated(null);
    try {
      const res = await authFetch(`${base}/v1/get-job-detail?job_id=${encodeURIComponent(run.JobID)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '',
      });
      if (res.status === 403) { navigate('/login'); return; }
      if (!res.ok) { setLogContent([`Failed to load log (${res.status}).`]); return; }
      const data = await res.json();
      const logStr = (data.JobLog || '').replace(/\\n/g, '\n');
      const lines = logStr.split('\n').map((l) => l.replace(/^"|"$/g, ''));
      const meta = [
        `Status:             ${data.Status ?? '—'}`,
        `Started:            ${fmtTime(data.StartTime)}`,
        `Finished:           ${fmtTime(data.FinishedTime)}`,
        `Resource Requested: ${data.ResourceRequested ?? '—'}`,
        '─── Output ───',
      ];
      setLogContent([...meta, ...lines]);
      if (data.LogTruncated) setLogTruncated({ totalBytes: data.LogTotalBytes });
      if (data.Status) setSelectedRunStatus(data.Status);
    } catch {
      setLogContent(['Error fetching log details.']);
    } finally {
      setLogLoading(false);
    }
  };

  const downloadLog = async () => {
    if (!selectedRun || downloading) return;
    setDownloading(true);
    try {
      await downloadJobLog(selectedRun);
    } catch (err) {
      setBanner({ type: 'error', text: err.message || 'Could not download the log.' });
    } finally {
      setDownloading(false);
    }
  };

  // ── form helpers ────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setModalOpen(true);
    if (mcrImages.length === 0) fetchMcrImages();
  };

  const openEdit = (cron) => {
    setEditingId(cron.cron_id);
    const rr = cron.resource_requests || {};
    const base = emptyForm();
    const parsed = parseCron(cron.schedule || '');
    let scheduleState = parsed
      ? { ...base, ...parsed, scheduleMode: 'ui', schedule: cron.schedule || '' }
      : { ...base, scheduleMode: 'cron', schedule: cron.schedule || '' };
    // The stored flag is authoritative for last-day-of-month jobs.
    if (cron.last_day_of_month) {
      scheduleState = { ...scheduleState, scheduleMode: 'ui', freq: 'monthly', monthDay: 'last' };
    }
    setForm({
      ...scheduleState,
      name: cron.name || '',
      image: cron.image || '',
      timezone: cron.timezone || 'UTC',
      args: parseArgs(cron.command_args),
      env: (cron.env && cron.env.length) ? cron.env.map(e => ({ name: e.name, value: e.value })) : [{ name: '', value: '' }],
      useConfigMap: !!cron.configmap_name,
      configMapName: cron.configmap_name || '',
      useSecret: !!cron.secret_name,
      secretName: cron.secret_name || '',
      useResources: !!(rr.cpu || rr.memory),
      cpu: rr.cpu != null ? String(rr.cpu) : '0.5',
      memory: rr.memory != null ? String(rr.memory) : '0.25',
      useGpu: !!cron.use_gpu,
      enabled: cron.enabled !== false,
    });
    setFormError(null);
    setModalOpen(true);
    if (mcrImages.length === 0) fetchMcrImages();
  };

  const setF = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // The effective cron string for the current form state.
  const currentSchedule = (f = form) => (f.scheduleMode === 'cron' ? (f.schedule || '').trim() : buildCron(f));

  const toggleScheduleMode = () => setForm(prev => {
    if (prev.scheduleMode === 'ui') {
      // Switching to raw cron: seed the textbox with the generated expression.
      return { ...prev, scheduleMode: 'cron', schedule: buildCron(prev) };
    }
    // Switching back to builder: try to parse what's in the textbox.
    const parsed = parseCron(prev.schedule);
    return parsed ? { ...prev, ...parsed, scheduleMode: 'ui' } : { ...prev, scheduleMode: 'ui' };
  });

  const toggleWeekday = (value) => setForm(prev => {
    const has = prev.weekdays.includes(value);
    const weekdays = has ? prev.weekdays.filter(d => d !== value) : [...prev.weekdays, value];
    return { ...prev, weekdays };
  });

  const updateArg = (i, field, v) => setForm(prev => ({ ...prev, args: prev.args.map((a, idx) => idx === i ? { ...a, [field]: v } : a) }));
  const addArg = () => setForm(prev => ({ ...prev, args: [...prev.args, { name: '', value: '' }] }));
  const removeArg = (i) => setForm(prev => ({ ...prev, args: prev.args.filter((_, idx) => idx !== i) }));

  const updateEnv = (i, field, v) => setForm(prev => ({ ...prev, env: prev.env.map((e, idx) => idx === i ? { ...e, [field]: v } : e) }));
  const addEnv = () => setForm(prev => ({ ...prev, env: [...prev.env, { name: '', value: '' }] }));
  const removeEnv = (i) => setForm(prev => ({ ...prev, env: prev.env.filter((_, idx) => idx !== i) }));

  const buildPayload = () => {
    const body = {
      name: form.name.trim(),
      schedule: currentSchedule(),
      image: form.image.trim(),
      timezone: (form.timezone || '').trim() || 'UTC',
      enabled: form.enabled,
      lastDayOfMonth: form.scheduleMode === 'ui' && form.freq === 'monthly' && form.monthDay === 'last',
    };
    const args = [];
    for (const row of form.args) {
      const k = (row.name || '').trim();
      const v = (row.value || '').trim();
      if (!k) continue;
      args.push(`--${k.replace(/^-+/, '')}`);
      if (v) args.push(v);
    }
    if (args.length) body.commandArgs = args;
    const env = form.env.filter(e => e.name.trim()).map(e => ({ name: e.name.trim(), value: e.value }));
    if (env.length) body.env = env;
    {
      // Send "" when cleared so an edit can detach a previously attached ConfigMap.
      const cm = form.useConfigMap ? form.configMapName.trim() : '';
      if (cm || editingId) body.configmapName = cm;
    }
    {
      // Same for the Secret: "" on an edit is how it gets detached.
      const sec = form.useSecret ? form.secretName.trim() : '';
      if (sec || editingId) body.secretName = sec;
    }
    if (form.useResources) {
      body.resourceRequests = { cpu: parseFloat(form.cpu) || 0.5, memory: parseFloat(form.memory) || 0.25 };
    }
    // Send the flag on every edit (not just when on) so unchecking it actually moves
    // the schedule back to the CPU pools — update only touches fields it receives.
    if (gpuAvailable && (form.useGpu || editingId)) body.useGpu = form.useGpu;
    return body;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!form.name.trim()) { setFormError('Name is required.'); return; }
    if (form.scheduleMode === 'ui' && form.freq === 'weekly' && form.weekdays.length === 0) {
      setFormError('Select at least one day of the week.'); return;
    }
    const sched = currentSchedule();
    if (!validateCron(sched)) { setFormError('Schedule is not a valid 5-field cron expression.'); return; }
    if (!form.image.trim()) { setFormError('An MCR image is required.'); return; }
    if (!isValidTimezone((form.timezone || '').trim())) { setFormError('Pick a valid timezone.'); return; }
    if (form.useConfigMap && !form.configMapName.trim()) {
      setFormError('Select a ConfigMap, or uncheck ConfigMap.'); return;
    }
    if (form.useSecret && !form.secretName.trim()) {
      setFormError('Select a Secret, or uncheck Secret.'); return;
    }

    setSubmitting(true);
    try {
      const url = editingId
        ? `${base}/v1/cron/update/${encodeURIComponent(editingId)}`
        : `${base}/v1/cron/create`;
      const res = await authFetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      await HandleResponse(res, navigate);
      if (res.ok) {
        setModalOpen(false);
        setBanner({ type: 'success', text: editingId ? 'Cron job updated.' : 'Cron job created.' });
        await fetchCrons();
        if (editingId && selectedCron?.cron_id === editingId) {
          const updated = await res.json().catch(() => null);
          if (updated) setSelectedCron(updated);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setFormError(data.detail || `Request failed (${res.status}).`);
      }
    } catch (err) {
      setFormError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCron = async (cron) => {
    try {
      const res = await authFetch(
        `${base}/v1/cron/toggle/${encodeURIComponent(cron.cron_id)}?enabled=${!cron.enabled}`,
        { method: 'PATCH' },
      );
      await HandleResponse(res, navigate);
      if (res.ok) {
        await fetchCrons();
        if (selectedCron?.cron_id === cron.cron_id) {
          setSelectedCron(prev => ({ ...prev, enabled: !cron.enabled }));
        }
      }
    } catch { /* ignore */ }
  };

  const requestDelete = (cron) => {
    setDeleteError(null);
    setConfirmDelete(cron);
  };

  const performDelete = async () => {
    const cron = confirmDelete;
    if (!cron) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await authFetch(`${base}/v1/cron/delete/${encodeURIComponent(cron.cron_id)}`, { method: 'DELETE' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        if (selectedCron?.cron_id === cron.cron_id) {
          setSelectedCron(null); setHistory([]); setSelectedRun(null); setLogContent(null);
        }
        setConfirmDelete(null);
        setBanner({ type: 'success', text: 'Cron job deleted.' });
        await fetchCrons();
      } else {
        const data = await res.json().catch(() => ({}));
        setDeleteError(data.detail || `Delete failed (${res.status}).`);
      }
    } catch (err) {
      setDeleteError(err.message || 'Delete failed. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const enabledCount = crons.filter(c => c.enabled !== false).length;

  return (
    <div className="cron-page">
      {banner && (
        <div className={`cron-banner cron-banner--${banner.type}`} onAnimationEnd={() => setBanner(null)}>
          {banner.text}
        </div>
      )}

      {/* ── Top toolbar ── */}
      <header className="cron-topbar">
        <div className="cron-topbar-left">
          <h1 className="cron-h1">Cron Jobs</h1>
          <p className="cron-sub">
            Recurring jobs scheduled inside your cluster
            <span className={`cron-cluster-badge ${isDedicated ? 'dedicated' : 'shared'}`}>
              {isDedicated ? 'Dedicated cluster' : 'Shared cluster'}
            </span>
          </p>
        </div>
        <button className="cron-btn-primary cron-btn-lg" onClick={openCreate}>
          <span className="cron-plus">+</span> New Cron Job
        </button>
      </header>

      <div className="cron-grid">
        {/* ── Pane 1: cron list ── */}
        <section className="cron-pane cron-pane--list">
          <div className="cron-pane-head">
            <span className="cron-pane-title">Configurations</span>
            <span className="cron-count">{enabledCount}/{crons.length} active</span>
          </div>
          <div className="cron-scroll">
            {cronsLoading && <div className="cron-empty">Loading…</div>}
            {!cronsLoading && crons.length === 0 && (
              <div className="cron-empty cron-empty--big">
                <div className="cron-empty-icon">⏱</div>
                <div className="cron-empty-title">No cron jobs yet</div>
                <div className="cron-empty-text">Create one to run a container on a schedule.</div>
                <button className="cron-btn-primary" onClick={openCreate}>+ New Cron Job</button>
              </div>
            )}
            {crons.map((c) => (
              <button
                key={c.cron_id}
                type="button"
                className={`cron-card ${selectedCron?.cron_id === c.cron_id ? 'active' : ''}`}
                onClick={() => selectCron(c)}
              >
                <div className="cron-card-top">
                  <span className="cron-card-name">
                    {c.last_error && <span className="cron-err-icon" title="Last run failed — click for details">⚠</span>}
                    {c.name}
                  </span>
                  <span className={`cron-pill ${c.enabled !== false ? 'on' : 'off'}`}>
                    {c.enabled !== false ? 'On' : 'Off'}
                  </span>
                </div>
                <div className="cron-card-bottom">
                  <span className="cron-sched-chip">{c.schedule}</span>
                  <span className="cron-card-image" title={c.image}>{c.image}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* ── Pane 2: executions ── */}
        <section className="cron-pane cron-pane--exec">
          {!selectedCron ? (
            <div className="cron-empty cron-empty--big cron-empty--center">
              <div className="cron-empty-icon">🗂</div>
              <div className="cron-empty-title">Select a cron job</div>
              <div className="cron-empty-text">Its recent executions will appear here.</div>
            </div>
          ) : (
            <>
              <div className="cron-pane-head">
                <span className="cron-pane-title" title={selectedCron.name}>{selectedCron.name}</span>
                <div className="cron-actions">
                  {/* Shows current state at rest and the action it performs on hover. */}
                  <button
                    className={`cron-chip-btn cron-toggle-btn ${selectedCron.enabled !== false ? 'on' : 'off'}`}
                    onClick={() => toggleCron(selectedCron)}
                    title={selectedCron.enabled !== false
                      ? 'Currently enabled — click to disable and stop the schedule'
                      : 'Currently disabled — click to enable and resume the schedule'}
                    aria-label={selectedCron.enabled !== false ? 'Disable this cron job' : 'Enable this cron job'}
                  >
                    <span className="cron-toggle-state">{selectedCron.enabled !== false ? 'Enabled' : 'Disabled'}</span>
                    <span className="cron-toggle-action">{selectedCron.enabled !== false ? 'Disable' : 'Enable'}</span>
                  </button>
                  <button className="cron-chip-btn" onClick={() => openEdit(selectedCron)}>Edit</button>
                  <button className="cron-chip-btn danger" onClick={() => requestDelete(selectedCron)}>Delete</button>
                </div>
              </div>

              {selectedCron.last_error && (
                <div className="cron-error-box">
                  <div className="cron-error-head">
                    <span className="cron-error-icon">⚠</span>
                    <span>Last run failed{selectedCron.last_error_at ? ` · ${relTime(selectedCron.last_error_at)}` : ''}</span>
                  </div>
                  <p className="cron-error-msg">{selectedCron.last_error}</p>
                  {selectedCron.enabled === false && (
                    <p className="cron-error-hint">
                      This job was automatically disabled. After fixing the issue above, click the
                      <strong> Disabled</strong> button at the top of this panel to turn it back on.
                    </p>
                  )}
                </div>
              )}

              <div className="cron-stats">
                <div className="cron-stat">
                  <span className="cron-stat-label">Schedule</span>
                  <span className="cron-stat-value"><code>{selectedCron.schedule}</code></span>
                </div>
                <div className="cron-stat">
                  <span className="cron-stat-label">Timezone</span>
                  <span className="cron-stat-value">{selectedCron.timezone || 'UTC'}</span>
                </div>
                <div className="cron-stat">
                  <span className="cron-stat-label">Image</span>
                  <span className="cron-stat-value cron-stat-mono" title={selectedCron.image}>{selectedCron.image}</span>
                </div>
                <div className="cron-stat">
                  <span className="cron-stat-label">Last run</span>
                  <span className="cron-stat-value">{fmtTime(selectedCron.last_triggered_at)}</span>
                </div>
                {selectedCron.configmap_name && (
                  <div className="cron-stat">
                    <span className="cron-stat-label">ConfigMap</span>
                    <span className="cron-stat-value cron-stat-mono" title={selectedCron.configmap_name}>{selectedCron.configmap_name}</span>
                  </div>
                )}
                {selectedCron.secret_name && (
                  <div className="cron-stat">
                    <span className="cron-stat-label">Secret</span>
                    <span className="cron-stat-value cron-stat-mono" title={selectedCron.secret_name}>{selectedCron.secret_name}</span>
                  </div>
                )}
              </div>

              <div className="cron-pane-subhead">Recent executions</div>
              <div className="cron-scroll">
                {historyLoading && <div className="cron-empty">Loading…</div>}
                {!historyLoading && history.length === 0 && (
                  <div className="cron-empty">No executions yet. The first run appears after the next scheduled tick.</div>
                )}
                {history.map((run) => (
                  <button
                    key={run.JobID}
                    type="button"
                    className={`cron-run ${selectedRun === run.JobID ? 'active' : ''}`}
                    onClick={() => selectRun(run)}
                  >
                    <span className={`cron-badge ${statusClass(run.Status)}`}>{run.Status || 'pending'}</span>
                    <div className="cron-run-meta">
                      <span className="cron-run-time">{fmtTime(run.StartTime)}</span>
                      <span className="cron-run-rel">{relTime(run.StartTime)}</span>
                    </div>
                    <span className="cron-run-arrow">›</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        {/* ── Pane 3: log ── */}
        <section className="cron-pane cron-pane--log">
          <div className="cron-log-head">
            <div className="cron-log-dots"><span /><span /><span /></div>
            <span className="cron-log-title">
              {selectedRun ? selectedRun : 'Execution log'}
            </span>
            {selectedRun && selectedRunStatus && (
              <span className={`cron-badge ${statusClass(selectedRunStatus)}`}>{selectedRunStatus}</span>
            )}
            {selectedRun && !logLoading && (
              <button
                className="jm-download-btn"
                onClick={downloadLog}
                disabled={downloading}
                title="Download the full log as a .log file"
              >
                {downloading ? 'Downloading…' : '⬇ Download Log'}
              </button>
            )}
          </div>
          <div className="cron-log-body">
            {logLoading && !logContent ? (
              <span className="cron-log-placeholder">Loading…</span>
            ) : logContent ? (
              <>
                {logTruncated && (
                  <div className="jm-log-truncated">
                    This log is {formatLogSize(logTruncated.totalBytes)} — too large to show in full,
                    so only the end is displayed. <strong>Download Log</strong> to view all of it.
                  </div>
                )}
                {logContent.map((line, i) => (
                  <div key={i} className={`cron-log-line ${line.startsWith('─') ? 'sep' : ''}`}>{line || '\u00A0'}</div>
                ))}
              </>
            ) : (
              <div className="cron-log-empty">
                <div className="cron-log-empty-icon">📄</div>
                <span>Select an execution to view its log</span>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ── Create / Edit modal ── */}
      {modalOpen && (
        <div className="cron-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div className="cron-modal" role="dialog" aria-modal="true">
            <div className="cron-modal-header">
              <span>{editingId ? 'Edit Cron Job' : 'New Cron Job'}</span>
              <button className="cron-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">×</button>
            </div>
            <form className="cron-form" onSubmit={submitForm} autoComplete="off">
              <div className="cron-field">
                <label className="cron-label">Name</label>
                <input className="cron-input" value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. nightly-backup" autoComplete="off" />
              </div>

              <div className="cron-field">
                <div className="cron-sched-headrow">
                  <label className="cron-label">Schedule</label>
                  <button type="button" className="cron-mode-toggle" onClick={toggleScheduleMode}>
                    {form.scheduleMode === 'ui' ? 'Use cron expression' : '‹ Back to builder'}
                  </button>
                </div>

                {form.scheduleMode === 'ui' ? (
                  <div className="cron-builder">
                    <div className="cron-freq-tabs">
                      {FREQS.map(fq => (
                        <button
                          type="button"
                          key={fq.id}
                          className={`cron-freq-tab ${form.freq === fq.id ? 'active' : ''}`}
                          onClick={() => setF('freq', fq.id)}
                        >
                          {fq.label}
                        </button>
                      ))}
                    </div>

                    <div className="cron-builder-body">
                      {form.freq === 'minutes' && (
                        <div className="cron-inline">
                          <span>Every</span>
                          <input type="number" min="1" max="59" className="cron-num" value={form.everyMinutes}
                            onChange={e => setF('everyMinutes', e.target.value)} />
                          <span>minute(s)</span>
                        </div>
                      )}
                      {form.freq === 'hourly' && (
                        <div className="cron-inline">
                          <span>Every</span>
                          <input type="number" min="1" max="23" className="cron-num" value={form.everyHours}
                            onChange={e => setF('everyHours', e.target.value)} />
                          <span>hour(s) at minute</span>
                          <input type="number" min="0" max="59" className="cron-num" value={form.hourlyMinute}
                            onChange={e => setF('hourlyMinute', e.target.value)} />
                        </div>
                      )}
                      {form.freq === 'daily' && (
                        <div className="cron-inline">
                          <span>At</span>
                          <TimeSelect value={form.timeOfDay} onChange={(v) => setF('timeOfDay', v)} />
                        </div>
                      )}
                      {form.freq === 'weekly' && (
                        <>
                          <div className="cron-weekdays">
                            {WEEKDAYS.map(d => (
                              <button
                                type="button"
                                key={d.value}
                                title={d.label}
                                className={`cron-wd ${form.weekdays.includes(d.value) ? 'active' : ''}`}
                                onClick={() => toggleWeekday(d.value)}
                              >
                                {d.short}
                              </button>
                            ))}
                          </div>
                          <div className="cron-inline">
                            <span>At</span>
                            <TimeSelect value={form.timeOfDay} onChange={(v) => setF('timeOfDay', v)} />
                          </div>
                        </>
                      )}
                      {form.freq === 'monthly' && (
                        <>
                          <div className="cron-seg">
                            <button
                              type="button"
                              className={`cron-seg-btn ${form.monthDay === 'first' ? 'active' : ''}`}
                              onClick={() => setF('monthDay', 'first')}
                            >
                              Month start (1st)
                            </button>
                            <button
                              type="button"
                              className={`cron-seg-btn ${form.monthDay === 'last' ? 'active' : ''}`}
                              onClick={() => setF('monthDay', 'last')}
                            >
                              Last day of month
                            </button>
                          </div>
                          <div className="cron-inline">
                            <span>At</span>
                            <TimeSelect value={form.timeOfDay} onChange={(v) => setF('timeOfDay', v)} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <input
                    className="cron-input cron-input-mono"
                    value={form.schedule}
                    onChange={e => setF('schedule', e.target.value)}
                    placeholder="*/5 * * * *"
                    autoComplete="off"
                  />
                )}

                <div className={`cron-sched-preview ${validateCron(currentSchedule()) ? '' : 'invalid'}`}>
                  <span className="cron-sched-summary">{humanizeSchedule(currentSchedule())}</span>
                  <code className="cron-sched-expr">{currentSchedule() || '—'}</code>
                </div>
              </div>

              <div className="cron-field">
                <label className="cron-label">MCR Image{mcrLoading && <span className="cron-hint" style={{ marginLeft: 8 }}>refreshing…</span>}</label>
                <select
                  className="cron-input"
                  value={form.image}
                  onChange={e => setF('image', e.target.value)}
                  onMouseDown={fetchMcrImages}
                  onFocus={fetchMcrImages}
                >
                  <option value="">— select an MCR image —</option>
                  {form.image && !mcrLoading && !mcrImages.includes(form.image) && (
                    <option value={form.image}>{form.image} — no longer in registry</option>
                  )}
                  {mcrImages.map(img => <option key={img} value={img}>{img}</option>)}
                </select>
                {form.image && !mcrLoading && !mcrImages.includes(form.image) && (
                  <p className="cron-hint cron-hint-block" style={{ color: '#dc2626' }}>
                    ⚠ "{form.image}" is no longer in the registry. Select a valid image before saving,
                    otherwise this cron job will fail to run.
                  </p>
                )}
                <p className="cron-note">
                  <span className="cron-note-icon">🔒</span>
                  Only images from the Mermadic Container Registry (MCR) are allowed. Cron jobs run
                  unattended, so Mermadic never stores your private registry credentials — push your
                  image to MCR and it runs here with zero secrets.
                </p>
              </div>

              <div className="cron-field">
                <label className="cron-label">Timezone</label>
                <select className="cron-input" value={form.timezone} onChange={e => setF('timezone', e.target.value)}>
                  {form.timezone && !TIMEZONES.includes(form.timezone) && (
                    <option value={form.timezone}>{form.timezone}</option>
                  )}
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <p className="cron-hint cron-hint-block">Schedule times are interpreted in this timezone.</p>
              </div>

              <div className="cron-field">
                <label className="cron-label">Command Arguments <span className="cron-hint">(flag name → value)</span></label>
                {form.args.map((a, i) => (
                  <div className="cron-kv-row" key={i}>
                    <input className="cron-input cron-input-mono" value={a.name} onChange={e => updateArg(i, 'name', e.target.value)} placeholder="flag name" autoComplete="off" />
                    <input className="cron-input" value={a.value} onChange={e => updateArg(i, 'value', e.target.value)} placeholder="value" autoComplete="off" />
                    <button type="button" className="cron-kv-remove" onClick={() => removeArg(i)} title="Remove">×</button>
                  </div>
                ))}
                <button type="button" className="cron-kv-add" onClick={addArg}>+ Add argument</button>
              </div>

              <div className="cron-field">
                <label className="cron-label">Environment Variables</label>
                {form.env.map((e2, i) => (
                  <div className="cron-kv-row" key={i}>
                    <input className="cron-input cron-input-mono" value={e2.name} onChange={e => updateEnv(i, 'name', e.target.value)} placeholder="NAME" autoComplete="off" />
                    <input className="cron-input" value={e2.value} onChange={e => updateEnv(i, 'value', e.target.value)} placeholder="value" autoComplete="off" />
                    <button type="button" className="cron-kv-remove" onClick={() => removeEnv(i)} title="Remove">×</button>
                  </div>
                ))}
                <button type="button" className="cron-kv-add" onClick={addEnv}>+ Add variable</button>
              </div>

              <div className="cron-field">
                <label className="cron-toggle">
                  <input
                    type="checkbox"
                    checked={form.useConfigMap}
                    onChange={e => setF('useConfigMap', e.target.checked)}
                  />
                  ConfigMap
                  {!isDedicated && <span className="cj-dedicated-badge">stored as env file on shared</span>}
                </label>
                {!form.useConfigMap && <span className="cron-hint cron-hint-block">Enable to load environment variables from an existing ConfigMap on every run.</span>}
                {form.useConfigMap && (
                  <select
                    className="cron-input"
                    value={form.configMapName}
                    onChange={e => setF('configMapName', e.target.value)}
                  >
                    <option value="">
                      {configMapsLoading ? 'Loading…' : configMaps.length === 0 ? '— no ConfigMaps found —' : '— select a ConfigMap —'}
                    </option>
                    {configMaps.map(cm => <option key={cm} value={cm}>{cm}</option>)}
                  </select>
                )}
              </div>

              <div className="cron-field">
                <label className="cron-toggle">
                  <input
                    type="checkbox"
                    checked={form.useSecret}
                    onChange={e => setF('useSecret', e.target.checked)}
                  />
                  Secret
                  <span className="cj-dedicated-badge">write-only</span>
                </label>
                {!form.useSecret && <span className="cron-hint cron-hint-block">Enable to inject API keys, passwords or tokens from a Secret on every run.</span>}
                {form.useSecret && (
                  <select
                    className="cron-input"
                    value={form.secretName}
                    onChange={e => setF('secretName', e.target.value)}
                  >
                    <option value="">
                      {secretsLoading ? 'Loading…' : secrets.length === 0 ? '— no Secrets found —' : '— select a Secret —'}
                    </option>
                    {secrets.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </div>

              <div className="cron-field">
                <label className="cron-toggle">
                  <input type="checkbox" checked={form.useResources} onChange={e => setF('useResources', e.target.checked)} />
                  Resource Requests
                </label>
                {form.useResources && (
                  <div className="cron-field-2col">
                    <div className="cron-field">
                      <label className="cron-label-sm">CPU (vCPU)</label>
                      <input className="cron-input" type="number" step="0.1" min="0.1" value={form.cpu} onChange={e => setF('cpu', e.target.value)} />
                    </div>
                    <div className="cron-field">
                      <label className="cron-label-sm">Memory (GB)</label>
                      <input className="cron-input" type="number" step="0.25" min="0.25" value={form.memory} onChange={e => setF('memory', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              {gpuAvailable && (
                <div className="cron-field">
                  <label className="cron-toggle">
                    <input type="checkbox" checked={form.useGpu} onChange={e => setF('useGpu', e.target.checked)} />
                    Use GPU <span className="cron-hint">(1&times; NVIDIA T4)</span>
                  </label>
                  {form.useGpu && (
                    <span className="cron-hint">
                      Every run is billed per GPU-hour (15-minute minimum) on top of CPU and
                      memory usage. Capped at 3 vCPU / 12 GB.
                    </span>
                  )}
                </div>
              )}

              <div className="cron-field">
                <label className="cron-toggle">
                  <input type="checkbox" checked={form.enabled} onChange={e => setF('enabled', e.target.checked)} />
                  Enabled <span className="cron-hint">(schedule active)</span>
                </label>
              </div>

              {formError && <p className="cron-form-error">{formError}</p>}

              <div className="cron-modal-actions">
                <button type="button" className="cron-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="cron-btn-primary" disabled={submitting}>
                  {submitting ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Cron Job')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      {confirmDelete && (
        <div
          className="cron-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !deleting) setConfirmDelete(null); }}
        >
          <div className="cron-modal cron-modal--confirm" role="dialog" aria-modal="true">
            <div className="cron-modal-header">
              <span>Delete cron job</span>
              <button
                className="cron-modal-close"
                onClick={() => !deleting && setConfirmDelete(null)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="cron-confirm-body">
              <div className="cron-confirm-icon">⚠</div>
              <div className="cron-confirm-text">
                <p>
                  Delete cron job <strong>“{confirmDelete.name}”</strong>?
                </p>
                <p className="cron-confirm-sub">
                  This stops the schedule from running again. Past run logs are kept.
                </p>
              </div>
            </div>
            {deleteError && <p className="cron-form-error cron-confirm-error">{deleteError}</p>}
            <div className="cron-modal-actions cron-confirm-actions">
              <button
                type="button"
                className="cron-btn-secondary"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cron-btn-danger"
                onClick={performDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete cron job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
